import { Box, Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CommentSkeleton, isWhitespace, MeetingNote, TextEditor, type TextEditorRef } from ".";
import { GetMeetingNotesDto } from "../http/types/meetings/notes";

const DRAFTS_STORAGE_KEY = "meeting-notes-drafts";

function getDrafts(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DRAFTS_STORAGE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function setDraft(key: string, text: string) {
  const drafts = getDrafts();
  if (text) {
    drafts[key] = text;
  } else {
    delete drafts[key];
  }
  localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
}

function clearDraft(key: string) {
  const drafts = getDrafts();
  delete drafts[key];
  localStorage.setItem(DRAFTS_STORAGE_KEY, JSON.stringify(drafts));
}

interface IProps {
  meetingGuid: string;
  data: GetMeetingNotesDto | undefined;
  isLoading: boolean;
  isPrivate: boolean;
  onSendClicked: (text: string) => void;
}

const MeetingNotesTabPanel = (props: IProps) => {
  const draftKey = `${props.meetingGuid}-${props.isPrivate ? "private" : "shared"}`;
  const [text, setText] = useState<string>(() => getDrafts()[draftKey] ?? "");
  const theme = useTheme();
  const textEditor = useRef<TextEditorRef>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDraft(draftKey, text), 500);
    return () => clearTimeout(timer);
  }, [text, draftKey]);

  function handleSendClicked() {
    clearDraft(draftKey);
    textEditor.current?.setContent("");
    props.onSendClicked(text);
  }

  return (
    <Stack
      divider={<Divider />}
      sx={{ height: "100%", backgroundColor: props.isPrivate ? theme.palette.secondary.opacity10 : "transparent" }}
    >
      <Stack
        spacing={0.5}
        sx={{
          alignItems: "flex-end",
          p: 1
        }}>
        <TextEditor
          ref={textEditor}
          initialValue={text}
          placeholder={props.isPrivate ? "Enter a private note..." : "Enter a note to discuss in the meeting..."}
          onChange={(value) => setText(value)}
        />
        <Box>
          <Button variant="contained" disabled={isWhitespace(text)} onClick={handleSendClicked}>
            {props.isPrivate ? "Save to private notes" : "Save to shared notes"}
          </Button>
        </Box>
      </Stack>

      {props.isLoading && (
        <Stack spacing={1} sx={{ m: 1 }}>
          {[...new Array(3)].map((_, i) => (
            <CommentSkeleton key={i} />
          ))}
        </Stack>
      )}

      {!props.isLoading && (
        <Stack divider={<Divider variant="dashed" sx={{ mx: 1 }} />} sx={{ overflowY: "auto" }}>
          {props.data?.meetingNotes.length === 0 ? (
            <Typography sx={{ m: 1 }}>Nothing here yet.</Typography>
          ) : (
            [...(props.data?.meetingNotes ?? [])]
              .sort((a, b) => {
                if (a.createdDateTimeUtc < b.createdDateTimeUtc) return 1;
                if (a.createdDateTimeUtc > b.createdDateTimeUtc) return -1;
                return 0;
              })
              .map((meetingNote) => (
                <MeetingNote
                  key={meetingNote.guid}
                  meetingGuid={props.meetingGuid}
                  meetingNote={meetingNote}
                  isPrivate={props.isPrivate}
                />
              ))
          )}
        </Stack>
      )}
    </Stack>
  );
};

export { MeetingNotesTabPanel };
